import * as React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import StaggerButton from '../StaggerButton';
import MorphLink from '../MorphLink';
import { LogoHorizontal } from '../Icons/Svg/LogoHorizontal';
import { LogoM } from '../Icons/Svg/LogoM';
import { CustomEase } from 'gsap/CustomEase';
import gsap from 'gsap';
import { useHomePage } from '../../contexts/HomePageContext';
import './animations.css';

gsap.registerPlugin(CustomEase);
CustomEase.create("flyIn", "M0,0 C0.208,1.07 0.625,0.982 0.846,1 0.901,1.004 1.003,1 1,1.005");
CustomEase.create("linkSwitchHeader", "M0,0 C0.084,0.61 0.214,0.802 0.28,0.856 0.356,0.918 0.374,1 1,1");

{/* 
============================================================================
  HEADER NAVIGATION MORPHING TEXT ANIMATION
  - Text slides up/down on hover
  - Dot stretches and contracts
  - Used in desktop navigation links
============================================================================
*/}
const headerStyles = `
  /* Text Morph Animation */
  .text-morph {
    position: relative;
    width: auto;
    width: fit-content;
    height: 20px;
    display: inline-flex;
    align-items: center;
    color: var(--header-text-theme, var(--BlackBrand));
    font-family: "Switzer", sans-serif;
    font-style: normal;
    font-weight: 550;
    /* text-transform: uppercase; */
  }

  .text-morph .text-container {
    position: relative;
    width: auto;
    width: fit-content;
    height: 20px;
    overflow: hidden;
    margin-left: 1.25em;
    text-align: left;
    display: flex;
    align-items: center;
  }

  /* Ensure all text content inherits the dynamic color */
  .text-morph span,
  .text-morph [data-text],
  .text-morph [data-text] span {
    color: inherit;
  }

  .text-morph:hover span,
  .text-morph.active span {
    color: var(--header-text-hover-theme, var(--GoldBrand-dr-2));
  }

  /* Stagger animation for character spans created by MorphLink */
  .text-morph [data-text] span {
    display: inline-block;
    position: relative;
    text-shadow: 0px 1.3em currentColor;
    transform: translateY(0em) rotate(0.001deg);
    transition: transform 0.6s cubic-bezier(0.625, 0.05, 0, 1);
  }

  .text-morph:hover [data-text] span {
    transform: translateY(-1.3em) rotate(0.001deg);
  }

  /* Stagger delays for smooth character animation */
  .text-morph [data-text] span:nth-child(1) { transition-delay: 0.01s; }
  .text-morph [data-text] span:nth-child(2) { transition-delay: 0.02s; }
  .text-morph [data-text] span:nth-child(3) { transition-delay: 0.03s; }
  .text-morph [data-text] span:nth-child(4) { transition-delay: 0.04s; }
  .text-morph [data-text] span:nth-child(5) { transition-delay: 0.05s; }
  .text-morph [data-text] span:nth-child(6) { transition-delay: 0.06s; }
  .text-morph [data-text] span:nth-child(7) { transition-delay: 0.07s; }
  .text-morph [data-text] span:nth-child(8) { transition-delay: 0.08s; }
  .text-morph [data-text] span:nth-child(9) { transition-delay: 0.09s; }
  .text-morph [data-text] span:nth-child(10) { transition-delay: 0.10s; }
  .text-morph [data-text] span:nth-child(11) { transition-delay: 0.11s; }
  .text-morph [data-text] span:nth-child(12) { transition-delay: 0.12s; }
  .text-morph [data-text] span:nth-child(13) { transition-delay: 0.13s; }
  .text-morph [data-text] span:nth-child(14) { transition-delay: 0.14s; }
  .text-morph [data-text] span:nth-child(15) { transition-delay: 0.15s; }

  .text-morph .circle {
    position: absolute;
    width: 6px;
    height: 6px;
    background-color: var(--header-text-hover-theme, var(--GoldBrand-dr-2));
    border-radius: 50%;
    left: 0;
    top: 50%;
    transform: translateY(-50%) translateX(0);
    z-index: 10;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  /* Show dot on hover */
  .text-morph:hover .circle {
    opacity: 1;
    background-color: var(--header-text-hover-theme, var(--GoldBrand-dr-2));
  }

  /* Quick extend animation on active */
  .text-morph.active .circle {
    opacity: 1;
    background-color: var(--header-text-hover-theme, var(--GoldBrand-dr-2));
    animation: dotExtendContract 0.2s cubic-bezier(0.65, 0.05, 0, 1) forwards;
  }

  @keyframes dotExtendContract {
    0% {
      transform: translateY(-50%) translateX(0);
      width: 6px;
      border-radius: 50%;
    }
    35% {
      transform: translateY(-50%) translateX(-20px);
      width: 15px;
      border-radius: 4px;
    }
    100% {
      transform: translateY(-50%) translateX(0);
      width: 6px;
      border-radius: 50%;
    }
  }

  /* Return animation for the circle */
  .text-morph:not(.active) .circle {
    animation: none;
  }

  /* Mobile viewport height fix */
  :root {
    --vh: 1vh;
  }

  @supports (-webkit-touch-callout: none) {
    .min-h-screen {
      min-height: -webkit-fill-available;
    }
  }
`;

interface HeaderProps {
  onGetStarted?: () => void;
}

const Header = ({ onGetStarted }: HeaderProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isHomePage, isModalOpen } = useHomePage();
  
  // Section-based background detection - more reliable than color sampling
  const [isDarkBackground, setIsDarkBackground] = React.useState(false);
  const [currentSectionId, setCurrentSectionId] = React.useState('');
  
  // Set up section-based background detection on any page that has tracked sections
  React.useEffect(() => {
    // Add IDs here when a section needs the header to flip to its dark theme.
    // Anything not listed defaults to light.
    const DARK_SECTIONS = new Set<string>([
      'cta-section',             // Home CTA (BlackBrand-br-3)
      'site-footer',
      'about-cta-section',       // About CTA (BlackBrand-br-3)
      'navigator-main-section',  // Navigator hero (BlackBrand)
    ]);

    const smoothWrapper = document.getElementById('smooth-wrapper');
    if (!smoothWrapper) return;

    // Re-query sections every check so lazy-loaded routes are picked up once mounted.
    const checkCurrentSection = () => {
      const headerBottom = 80; // Header height
      const sections = document.querySelectorAll<HTMLElement>('section[id], footer[id]');

      for (const section of sections) {
        const rect = section.getBoundingClientRect();
        if (rect.top <= headerBottom && rect.bottom > headerBottom) {
          const sectionId = section.id;
          setIsDarkBackground(DARK_SECTIONS.has(sectionId));
          setCurrentSectionId(sectionId);
          return;
        }
      }

      // Nothing matched (e.g. between sections, or page still loading) — default light.
      setIsDarkBackground(false);
      setCurrentSectionId('');
    };

    // Check immediately, then again after a tick to catch lazy-loaded routes.
    checkCurrentSection();
    const initialRetryId = setTimeout(checkCurrentSection, 100);
    const lazyRetryId = setTimeout(checkCurrentSection, 500);

    smoothWrapper.addEventListener('scroll', checkCurrentSection, { passive: true });

    return () => {
      smoothWrapper.removeEventListener('scroll', checkCurrentSection);
      clearTimeout(initialRetryId);
      clearTimeout(lazyRetryId);
    };
  }, [location.pathname]);

  // Mobile menu state - ensure it starts as closed
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // Ensure menu is closed on mount and route changes
  React.useEffect(() => {
    setMobileMenuOpen(false);
    document.body.setAttribute('data-nav-status', 'closed');
  }, [location.pathname]);

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    const newStatus = !mobileMenuOpen ? 'active' : 'closed';
    document.body.setAttribute('data-nav-status', newStatus);

    // Mobile menu scroll handling - prevent body scroll when menu is open
    // This is handled by the body overflow styles in the useEffect below
  };

  const handleGetStarted = (e: React.MouseEvent) => {
    e.preventDefault();
    onGetStarted?.();
    if (mobileMenuOpen) {
      toggleMobileMenu();
    }
  };

  const handleScrollToSection = (sectionId: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    if (mobileMenuOpen) {
      toggleMobileMenu();
    }

    const scrollToEl = () => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    };

    if (location.pathname === '/') {
      scrollToEl();
    } else {
      navigate('/');
      setTimeout(scrollToEl, 400);
    }
  };

  // Handle navigation with scroll to top
  const handleNavClick = (to: string) => (e: React.MouseEvent) => {
    e.preventDefault();

    // Navigate immediately for hash links
    if (to.includes('#')) {
      navigate(to);
      return;
    }

    const smoothWrapper = document.getElementById('smooth-wrapper');

    // Close mobile menu if open first
    if (mobileMenuOpen) {
      toggleMobileMenu();
    }

    // If clicking same page, manually scroll to top instantly
    if (location.pathname === to) {
      if (smoothWrapper) {
        smoothWrapper.style.scrollSnapType = 'none';
        smoothWrapper.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => {
          if (to === '/') {
            smoothWrapper.style.scrollSnapType = 'none';
          }
        }, 300);
      }
      return;
    }

    // Disable scroll snap temporarily to allow smooth navigation
    if (smoothWrapper) {
      smoothWrapper.style.scrollSnapType = 'none';
    }

    // Navigate (ScrollToTop component in App.tsx handles scroll-to-top)
    navigate(to);
    
    // Re-enable scroll snap after navigation (only for homepage)
    setTimeout(() => {
      if (smoothWrapper && to === '/') {
        smoothWrapper.style.scrollSnapType = 'none';
      }
    }, 100);
  };

  // Handle body overflow when mobile menu is open
  React.useEffect(() => {
    const smoothWrapper = document.getElementById('smooth-wrapper');
    
    if (mobileMenuOpen) {
      // Lock both body and smooth wrapper
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      if (smoothWrapper) {
        smoothWrapper.style.overflow = 'hidden';
      }
    } else {
      // Restore normal scrolling
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      if (smoothWrapper) {
        smoothWrapper.style.overflow = '';
      }
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      if (smoothWrapper) {
        smoothWrapper.style.overflow = '';
      }
    };
  }, [mobileMenuOpen]);

  // Set CSS custom properties for dynamic theming based on the section under the header
  React.useEffect(() => {
    const textTheme = isDarkBackground ? 'var(--WhiteBrand-br-90)' : 'var(--BlackBrand)';
    const textOppositeTheme = isDarkBackground ? 'var(--BlackBrand)' : 'var(--WhiteBrand-br-90)';
    const textHoverTheme = isDarkBackground ? 'var(--GoldBrand)' : 'var(--GoldBrand-dr-2)';

    document.documentElement.style.setProperty('--header-text-theme', textTheme);
    document.documentElement.style.setProperty('--header-text-opposite-theme', textOppositeTheme);
    document.documentElement.style.setProperty('--header-text-hover-theme', textHoverTheme);
  }, [isDarkBackground]);

  // Add the styles to the document head
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = headerStyles;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Add viewport height calculation
  React.useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVH();
    window.addEventListener('resize', setVH);
    return () => window.removeEventListener('resize', setVH);
  }, []);

  

  return (
    <header
      className={`fixed w-full top-0 left-0 z-[1100] ${isModalOpen ? 'max-mobile:pointer-events-none' : ''}`}
      data-nav-status={mobileMenuOpen ? 'active' : 'closed'}
    >
      {/* Blur effect layer */}
      <div className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${
        mobileMenuOpen ? 'opacity-[0.90] backdrop-blur-[70px]' : ' opacity-[1] backdrop-blur-[40px]'
      } header-overlay`}></div>

      <div className="container max-w-[1408px] mx-auto relative">
        <nav className="flex flex-row justify-between items-center h-[4em] relative px-[64px]
        max-tablet:pl-[64px] max-tablet:pr-[32px]
        max-mobile:px-[16px]" aria-label="Main navigation">
          <LogoHorizontal className="hidden absolute inset-0 h-[1.15em] left-5 top-1/2 -translate-y-1/2 opacity-50" />
          <Link to="/" onClick={handleNavClick("/")} className="flex flex-row items-center gap-[0.25em] transition-colors
          max-tablet:gap-[0.25em]
          max-mobile:gap-[0.25em]" aria-label="Maeven home">
            <div className="relative h-full flex items-center overflow-hidden">
              <div id="logo" className="relative text-[var(--header-text-theme)] font-logoFont font-[650] text-[1.75em] tracking-[-0.01em] whitespace-nowrap
              max-tablet:text-[1.75em] max-tablet:font-[650]
              max-mobile:text-[1.5em]">maeven</div>
            </div>
            <div id="logoWrapper" className="relative flex items-center justify-center overflow-visible -mb-[0.2em] w-[28px] h-[28px]
            max-mobile:w-[24px] max-mobile:h-[24px]">
              <LogoM iconColor="var(--header-text-hover-theme)" className="ml-[0.125em] logo-m logo-m-hover w-full h-auto block" />
            </div>
          </Link>
          {/* Hidden: Divider line */}
          <div className="header-link mx-[64px] h-[0.0625em] bg-[var(--WhiteBrand-br-70)] flex-grow hidden
          max-tablet:mx-[32px]
          max-mobile:hidden"></div>

          {/* Desktop Navigation - Centered */}
          <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-[2em]
          max-tablet:hidden">
              <MorphLink
                defaultText="Platform"
                href="/"
                onClick={handleNavClick("/")}
                isActive={location.pathname === '/' && currentSectionId !== 'security-section'}
                aria-current={location.pathname === '/' && currentSectionId !== 'security-section' ? 'page' : undefined}

                className="font-main font-[500] stagger-link header-link"
              />
              <MorphLink
                defaultText="Security"
                href="/#security-section"
                onClick={handleScrollToSection('security-section')}
                isActive={isHomePage && currentSectionId === 'security-section'}
                className="font-main font-[500] stagger-link header-link"
              />
              <MorphLink
                defaultText="Community"
                href="/events"
                onClick={handleNavClick("/events")}
                isActive={location.pathname === '/events'}
                aria-current={location.pathname === '/events' ? 'page' : undefined}
                className="font-main font-[500] stagger-link header-link"
              />
              <MorphLink
                defaultText="About"
                href="/about"
                onClick={handleNavClick("/about")}
                isActive={location.pathname === '/about'}
                aria-current={location.pathname === '/about' ? 'page' : undefined}
                className="font-main font-[500] stagger-link header-link"
              />
              <MorphLink
                defaultText="FAQs"
                href="/faq"
                onClick={handleNavClick("/faq")}
                isActive={location.pathname === '/faq'}
                aria-current={location.pathname === '/faq' ? 'page' : undefined}
                className="font-main font-[500] stagger-link header-link"
              />
          </div>

          {/* Action Buttons + Mobile Menu */}
          <div className="flex items-center">
            {/* Action Buttons */}
            <div className="flex items-center justify-between gap-[16px]
            max-tablet:hidden">
              <StaggerButton
                onClick={() => window.dispatchEvent(new CustomEvent('open-lets-talk-modal'))}
                variant="secondary"
                height="h-[2.5em]"
                px="px-[2em]"
                bgColor='transparent'
                textColor='var(--header-text-theme)'
                className="max-w-[128px] w-[128px] stagger-button flex items-center justify-center shadow-[inset_0_0_0_0.09375em_var(--header-text-theme)] font-deco italic
                max-tablet:hidden max-tablet:flex"
              >
                Let's talk
              </StaggerButton>
              <Link to="/getStarted" onClick={handleGetStarted}>
                <StaggerButton
                  variant="primary"
                  height="h-[2.5em]"
                  px="px-[2em]"

                  className="max-w-[128px] w-[128px] stagger-button flex items-center justify-center
                  max-tablet:hidden max-tablet:flex"
                >
                  Get started
                </StaggerButton>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className={`flex items-center justify-center w-[3em] h-[3em] rounded-[0.25em] relative z-[991] transition-all hidden max-tablet:flex ${isModalOpen ? 'pointer-events-none opacity-0' : ''}`}
              onClick={toggleMobileMenu}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
            >
              <div className="w-5 h-5 relative">
                <span className={`absolute w-5 h-0.5 bg-[var(--GoldBrand-dr-2)] transform transition-all duration-300 ${mobileMenuOpen ? 'rotate-45 top-2 right-0' : 'top-1 right-0'}`}></span>
                <span className={`absolute w-5 h-0.5 bg-[var(--GoldBrand-dr-2)] transform transition-all duration-300 ${mobileMenuOpen ? '-rotate-45 top-2 right-0' : 'top-3 right-0'}`}></span>
              </div>
            </button>
          </div>
        </nav>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        id="mobile-menu"
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation menu"
        className={`fixed inset-0 top-[60px] bg-[var(--GoldBrand-dr-3)] z-[1100] h-[calc(100*var(--vh)_-_60px)] overflow-y-auto transition-all duration-500 ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-800/90 to-black/50"></div>
        <div className="w-full px-4 pt-8 relative z-[990]">
          <div className="nav-menu" data-nav-status={mobileMenuOpen ? "active" : "inactive"}>
            <div className="space-y-[1.5em] mb-[3em]">
              <Link to="/" onClick={handleNavClick("/")} aria-current={location.pathname === '/' ? 'page' : undefined} className="block hamburger-nav__a relative overflow-hidden group py-[0.75em] mx-[1.5em]">
                <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <p className="text-lg18 font-main font-[500] text-[var(--WhiteBrand-br-80)] hamburger-nav__p">Platform</p>
              </Link>

              <a
                href="/#security-section"
                onClick={handleScrollToSection('security-section')}
                className="block hamburger-nav__a relative overflow-hidden group py-[0.75em] mx-[1.5em]"
              >
                <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <p className="text-lg18 font-main font-[500] text-[var(--WhiteBrand-br-80)] hamburger-nav__p">Security</p>
              </a>

              <Link to="/events" onClick={handleNavClick("/events")} aria-current={location.pathname === '/events' ? 'page' : undefined} className="block hamburger-nav__a relative overflow-hidden group py-[0.75em] mx-[1.5em]">
                <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <p className="text-lg18 font-main font-[500] text-[var(--WhiteBrand-br-80)] hamburger-nav__p">Community</p>
              </Link>

              <Link to="/about" onClick={handleNavClick("/about")} aria-current={location.pathname === '/about' ? 'page' : undefined} className="block hamburger-nav__a relative overflow-hidden group py-[0.75em] mx-[1.5em]">
                <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <p className="text-lg18 font-main font-[500] text-[var(--WhiteBrand-br-80)] hamburger-nav__p">About</p>
              </Link>

              <Link to="/faq" onClick={handleNavClick("/faq")} aria-current={location.pathname === '/faq' ? 'page' : undefined} className="block hamburger-nav__a relative overflow-hidden group py-[0.75em] mx-[1.5em]">
                <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <p className="text-lg18 font-main font-[500] text-[var(--WhiteBrand-br-80)] hamburger-nav__p">FAQ</p>
              </Link>
            </div>

            <div className="flex flex-col gap-[1.5em] px-[1.5em]">
              <Link to="/getStarted" onClick={handleGetStarted}>
                <StaggerButton
                  variant="primary"
                  height="h-[3.5em]"
                  className="w-full font-[600] !text-base leading-[1.5em]"
                >
                  Get started
                </StaggerButton>
              </Link>
              <StaggerButton
                onClick={() => {
                  if (mobileMenuOpen) toggleMobileMenu();
                  setTimeout(() => window.dispatchEvent(new CustomEvent('open-lets-talk-modal')), 300);
                }}
                variant="secondary"
                textColor="var(--BlackBrand)"
                height="h-[3.5em]"
                bgColor="var(--WhiteBrand-br-90)"
                className="w-full border-[0.09375em] border-[var(--WhiteBrand-br-90)] font-[600] !text-base leading-[2em]"
              >
                Let's talk
              </StaggerButton>
              {/* Hidden: Log in button */}
              <a href="https://app.maeven.ai/auth/signin?redirectUrl=%2F" className="hidden">
                <StaggerButton
                  variant="secondary"
                  textColor="var(--WhiteBrand)"
                  height="h-[3.5em]"
                  bgColor="var(--BlackBrand)"
                  className="w-full border-[0.09375em] border-[var(--BlackBrand)] font-[600] !text-base leading-[2em]"
                >
                  Log in
                </StaggerButton>
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
